import React, { useMemo } from 'react';
import { Edit3 } from 'lucide-react';
import type {
  EstructuraArmarPedido,
  FilaMatrizPedido,
  CeldaMatrizPedido,
  TipoPlatoPedido,
} from '../../types/pedidosNuevos';

/**
 * GrillaSemanal — vista semanal estilo minuta QuickTaste.
 *
 * Layout:
 *   - Filas = categorias / tipos de plato del contrato del cliente.
 *     Cuando un tipo tiene multiples opciones (varias filas con el mismo
 *     tipoPlato.id pero distinto filaOrden), se agrupan en UNA fila
 *     visual y las opciones se apilan dentro de cada celda dia.
 *   - Columnas = dias de la minuta activa.
 *   - Celda = el/los plato(s) asignado(s) a ese tipo+dia. Cada plato
 *     tiene nombre, descripcion corta (descripcion_menu), badge
 *     compuesto si aplica, y una cajita numerica de cantidad
 *     (InputCantidadPlato-style minimal: <input type=number/>).
 *   - Bebidas: seccion aparte (igual que la carta), una tabla horizontal
 *     pequena con totales y validacion bebidas <= comida.
 *
 * Branding NexGen: cyan #29ABE2 + naranja #F7941D.
 *
 * Estado compartido con la vista carta — recibe los mismos
 * `cantidadesPorDia` + `handleCantidadChange` del padre, asi un valor
 * tipeado aqui aparece tambien en la carta y viceversa. NO mantiene
 * estado interno de cantidades.
 *
 * Responsive: en pantallas < md (768px) el padre cae a la vista carta;
 * este componente asume desktop y permite scroll horizontal si los dias
 * no entran.
 */
export interface GrillaSemanalProps {
  estructura: EstructuraArmarPedido;
  cantidadesPorDia: Map<string, Map<string, number>>;
  handleCantidadChange: (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number,
    cantidad: number
  ) => void;
  guardarBorradorDia: (fecha: string) => void;
  abrirModalConfirmarDia: (
    fecha: string,
    nombreDia: string,
    pedidoId: number | null
  ) => void;
  modificarPedidoConfirmado: (pedidoId: number, nombreDia: string) => void;
  guardandoDias: Set<string>;
  diasConCambios: Set<string>;
  claveCantidad: (tipoPlatoId: number, filaOrden: number) => string;
  bebidaTipoPlatoId: number;
  /** #5 (2026-07-01): total de porciones por día calculado en el padre.
   *  Gatea el botón "Confirmar" — no permite confirmar vacío. */
  totalPorDia?: Map<string, number>;
}

const ICONO_POR_TIPO_PLATO: Record<string, string> = {
  SOPA: '🍲',
  'COLACIÓN PRINCIPAL': '🍽️',
  'COLACIÓN PRINCIPAL 2': '🍽️',
  'COLACIÓN PRINCIPAL 3': '🍽️',
  HIPOCALÓRICO: '🥗',
  'DIETA BLANDA': '🍵',
  'ALMUERZO SIN GLUTEN': '🌾',
  CENA: '🌙',
  ENSALADA: '🥗',
  POSTRE: '🍰',
  DESAYUNO: '☕',
  SÁNDWICH: '🥪',
  SANDWICH: '🥪',
  ONCE: '☕',
  COLACIÓN: '🥡',
  COLACION: '🥡',
  BEBIDA: '🥤',
  BEBIDAS: '🥤',
  EXTRA: '✨',
  FONDO: '🍖',
  GUARNICIÓN: '🍚',
};

const iconoSeccion = (nombre: string): string => {
  const key = nombre.trim().toUpperCase();
  if (ICONO_POR_TIPO_PLATO[key]) return ICONO_POR_TIPO_PLATO[key];
  for (const [k, v] of Object.entries(ICONO_POR_TIPO_PLATO)) {
    if (key.includes(k)) return v;
  }
  return '🍴';
};

const obtenerColorEstado = (estado: string): string => {
  switch (estado) {
    case 'borrador':
      return 'bg-yellow-100 text-yellow-800';
    case 'solicitado':
      return 'bg-blue-100 text-blue-800';
    case 'confirmado':
      return 'bg-green-100 text-green-800';
    case 'cancelado':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-200 text-gray-700';
  }
};

interface GrupoTipo {
  tipoPlato: TipoPlatoPedido;
  filas: FilaMatrizPedido[];
}

const GrillaSemanal: React.FC<GrillaSemanalProps> = ({
  estructura,
  cantidadesPorDia,
  handleCantidadChange,
  guardarBorradorDia,
  abrirModalConfirmarDia,
  modificarPedidoConfirmado,
  guardandoDias,
  diasConCambios,
  claveCantidad,
  bebidaTipoPlatoId,
}) => {
  // Agrupar las filas de la matriz por tipoPlato.id — si un tipo tiene
  // multiples opciones (distinto filaOrden) las apilamos dentro de la
  // misma fila visual.
  const { gruposComida, gruposBebida } = useMemo<{
    gruposComida: GrupoTipo[];
    gruposBebida: GrupoTipo[];
  }>(() => {
    const mapa = new Map<number, GrupoTipo>();
    for (const fila of estructura.matriz) {
      const id = fila.tipoPlato.id;
      const g = mapa.get(id);
      if (g) {
        g.filas.push(fila);
      } else {
        mapa.set(id, { tipoPlato: fila.tipoPlato, filas: [fila] });
      }
    }
    const todos = Array.from(mapa.values()).sort(
      (a, b) => (a.tipoPlato.orden ?? 0) - (b.tipoPlato.orden ?? 0)
    );
    return {
      gruposComida: todos.filter((g) => g.tipoPlato.id !== bebidaTipoPlatoId),
      gruposBebida: todos.filter((g) => g.tipoPlato.id === bebidaTipoPlatoId),
    };
  }, [estructura, bebidaTipoPlatoId]);

  const getCantidad = (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number
  ): number =>
    cantidadesPorDia.get(fecha)?.get(claveCantidad(tipoPlatoId, filaOrden)) ?? 0;

  // Totales por dia para mostrar en el header y validar bebidas <= comida.
  const totalesPorDia = useMemo(() => {
    const out = new Map<string, { comida: number; bebidas: number }>();
    for (const dia of estructura.dias) {
      let comida = 0;
      let bebidas = 0;
      for (const grupo of gruposComida) {
        for (const fila of grupo.filas) {
          comida += getCantidad(dia.fecha, fila.tipoPlato.id, fila.tipoPlato.filaOrden);
        }
      }
      for (const grupo of gruposBebida) {
        for (const fila of grupo.filas) {
          bebidas += getCantidad(dia.fecha, fila.tipoPlato.id, fila.tipoPlato.filaOrden);
        }
      }
      out.set(dia.fecha, { comida, bebidas });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estructura, gruposComida, gruposBebida, cantidadesPorDia]);

  const renderCajitaCantidad = (
    fecha: string,
    fila: FilaMatrizPedido,
    celda: CeldaMatrizPedido,
    editable: boolean
  ) => {
    const cant = getCantidad(fecha, fila.tipoPlato.id, fila.tipoPlato.filaOrden);
    return (
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={cant === 0 ? '' : cant}
        disabled={!editable}
        onChange={(e) => {
          const v = parseInt(e.target.value || '0', 10);
          handleCantidadChange(
            fecha,
            fila.tipoPlato.id,
            fila.tipoPlato.filaOrden,
            isNaN(v) || v < 0 ? 0 : v
          );
        }}
        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right text-sm font-semibold focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2] disabled:opacity-60 disabled:cursor-not-allowed"
        placeholder="0"
        aria-label={`Cantidad ${fila.tipoPlato.nombre} ${
          celda.receta?.nombre ?? ''
        }`}
      />
    );
  };

  const renderOpcionPlato = (
    fecha: string,
    fila: FilaMatrizPedido,
    celda: CeldaMatrizPedido
  ) => {
    if (!celda.receta) {
      return (
        <div className="text-xs text-gray-400 italic text-center py-1">—</div>
      );
    }
    const editable = celda.estadoDia === 'borrador';
    const compuesto = !!celda.receta.recetaGuarnicion;
    const guarNombre = celda.receta.recetaGuarnicion?.nombre || '';
    const yaConcatenado =
      compuesto &&
      guarNombre &&
      celda.receta.nombre
        .toLowerCase()
        .includes(' con ' + guarNombre.toLowerCase());
    const nombrePlato =
      !compuesto || yaConcatenado
        ? celda.receta.nombre
        : `${celda.receta.nombre} con ${guarNombre}`;
    const descripcionMenu = (celda.receta.descripcion || '').trim();

    return (
      <div
        key={`${fila.tipoPlato.id}-${fila.tipoPlato.filaOrden}`}
        className="flex flex-col gap-1 py-1 border-b border-dashed border-gray-200 dark:border-gray-700 last:border-b-0"
      >
        <div className="flex items-start justify-between gap-1">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
              {nombrePlato}
            </div>
            {descripcionMenu && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5 line-clamp-2">
                {descripcionMenu}
              </div>
            )}
            {compuesto && (
              <span className="inline-block text-[9px] uppercase tracking-wider bg-[#F7941D]/15 text-[#F7941D] px-1.5 py-0.5 rounded font-semibold mt-1">
                compuesto
              </span>
            )}
          </div>
          {renderCajitaCantidad(fecha, fila, celda, editable)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0">
          {/* Cabecera de dias */}
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20 bg-gray-100 dark:bg-gray-700 px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200 border-b-2 border-[#29ABE2] min-w-[180px]"
                style={{ width: 180 }}
              >
                Categoría
              </th>
              {estructura.dias.map((dia) => {
                const totales = totalesPorDia.get(dia.fecha);
                const excede =
                  (totales?.bebidas ?? 0) > (totales?.comida ?? 0);
                return (
                  <th
                    key={dia.fecha}
                    className="bg-gray-100 dark:bg-gray-700 px-3 py-3 text-center min-w-[220px] border-b-2 border-[#29ABE2]"
                  >
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-gray-900 dark:text-white uppercase">
                        {dia.nombre}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {dia.fecha}
                      </div>
                      <div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded ${obtenerColorEstado(
                            dia.estado
                          )}`}
                        >
                          {dia.estado}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-gray-300">
                        <span className="font-semibold">
                          {totales?.comida ?? 0}
                        </span>{' '}
                        comida ·{' '}
                        <span
                          className={`font-semibold ${
                            excede ? 'text-red-600' : ''
                          }`}
                        >
                          {totales?.bebidas ?? 0}
                        </span>{' '}
                        beb
                      </div>
                      <div className="flex flex-col gap-1 pt-1">
                        {dia.estado === 'borrador' ? (
                          diasConCambios.has(dia.fecha) || !dia.pedidoId ? (
                            <button
                              onClick={() => guardarBorradorDia(dia.fecha)}
                              disabled={guardandoDias.has(dia.fecha)}
                              className="text-[11px] px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {guardandoDias.has(dia.fecha)
                                ? 'Guardando…'
                                : 'Guardar'}
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                abrirModalConfirmarDia(
                                  dia.fecha,
                                  dia.nombre,
                                  dia.pedidoId
                                )
                              }
                              disabled={!dia.pedidoId || excede}
                              title={
                                excede
                                  ? 'No puedes confirmar: bebidas > comida'
                                  : undefined
                              }
                              className="text-[11px] px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Confirmar
                            </button>
                          )
                        ) : dia.estado === 'solicitado' ? (
                          <button
                            onClick={() =>
                              dia.pedidoId &&
                              modificarPedidoConfirmado(
                                dia.pedidoId,
                                dia.nombre
                              )
                            }
                            className="text-[11px] px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center justify-center gap-1"
                          >
                            <Edit3 className="w-3 h-3" />
                            Modificar
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Filas: una por tipo de plato (comida) */}
          <tbody>
            {gruposComida.length === 0 && (
              <tr>
                <td
                  colSpan={1 + estructura.dias.length}
                  className="px-4 py-8 text-center text-gray-500 text-sm"
                >
                  Sin categorías con platos en esta minuta.
                </td>
              </tr>
            )}
            {gruposComida.map((grupo) => (
              <tr
                key={grupo.tipoPlato.id}
                className="border-b border-gray-100 dark:border-gray-700/40"
              >
                <td
                  className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 align-top"
                  style={{ width: 180 }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="inline-block w-1 rounded-full bg-[#F7941D] self-stretch min-h-[2.5rem]"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <div className="text-base leading-none mb-1">
                        {iconoSeccion(grupo.tipoPlato.nombre)}
                      </div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-white">
                        {grupo.tipoPlato.nombre}
                      </div>
                      {grupo.filas.length > 1 && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {grupo.filas.length} opciones
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                {estructura.dias.map((dia) => {
                  const opciones = grupo.filas
                    .map((fila) => ({
                      fila,
                      celda: fila.dias.find((d) => d.fecha === dia.fecha),
                    }))
                    .filter(
                      (x): x is { fila: FilaMatrizPedido; celda: CeldaMatrizPedido } =>
                        !!x.celda && !!x.celda.receta
                    );
                  return (
                    <td
                      key={dia.fecha}
                      className="px-2 py-2 align-top border-b border-gray-200 dark:border-gray-700"
                    >
                      {opciones.length === 0 ? (
                        <div className="text-xs text-gray-400 italic text-center py-1">
                          —
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {opciones.map(({ fila, celda }) =>
                            renderOpcionPlato(dia.fecha, fila, celda)
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Seccion bebidas */}
      {gruposBebida.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
          <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <span className="inline-block w-1 h-6 bg-[#29ABE2] rounded-full" />
            <span className="text-xl leading-none" aria-hidden="true">
              {iconoSeccion('BEBIDA')}
            </span>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              Bebidas
            </h3>
            <span className="ml-auto text-xs text-gray-500">
              (cada día: bebidas ≤ comida)
            </span>
          </header>
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700/40 px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 min-w-[180px]"
                  style={{ width: 180 }}
                >
                  Bebida
                </th>
                {estructura.dias.map((dia) => (
                  <th
                    key={dia.fecha}
                    className="bg-gray-50 dark:bg-gray-700/40 px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 min-w-[120px]"
                  >
                    {dia.nombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gruposBebida.flatMap((grupo) =>
                grupo.filas.map((fila) => (
                  <tr
                    key={`${fila.tipoPlato.id}-${fila.tipoPlato.filaOrden}`}
                    className="border-b border-gray-100 dark:border-gray-700/40"
                  >
                    <td
                      className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700"
                      style={{ width: 180 }}
                    >
                      {/* Nombre y descripcion vienen del primer dia que
                          tenga receta asignada para esta fila (filaOrden
                          define la opcion; la receta suele ser la misma
                          a lo largo de la semana). */}
                      {(() => {
                        const ref = fila.dias.find((d) => d.receta)?.receta;
                        return (
                          <>
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                              {ref?.nombre || fila.tipoPlato.nombre}
                            </div>
                            {ref?.descripcion && (
                              <div className="text-[10px] text-gray-500">
                                {ref.descripcion}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    {estructura.dias.map((dia) => {
                      const celda = fila.dias.find((d) => d.fecha === dia.fecha);
                      if (!celda) {
                        return (
                          <td
                            key={dia.fecha}
                            className="px-3 py-2 text-center text-xs text-gray-400 border-b border-gray-200 dark:border-gray-700"
                          >
                            —
                          </td>
                        );
                      }
                      const editable = celda.estadoDia === 'borrador';
                      return (
                        <td
                          key={dia.fecha}
                          className="px-3 py-2 text-center border-b border-gray-200 dark:border-gray-700"
                        >
                          {celda.receta
                            ? renderCajitaCantidad(
                                dia.fecha,
                                fila,
                                celda,
                                editable
                              )
                            : <span className="text-xs text-gray-400">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
              {/* Totales bebidas por dia */}
              <tr className="bg-gray-50 dark:bg-gray-700/30 font-semibold">
                <td
                  className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700/30 px-4 py-2 text-xs text-gray-700 dark:text-gray-200"
                  style={{ width: 180 }}
                >
                  Total bebidas
                </td>
                {estructura.dias.map((dia) => {
                  const totales = totalesPorDia.get(dia.fecha);
                  const excede =
                    (totales?.bebidas ?? 0) > (totales?.comida ?? 0);
                  return (
                    <td
                      key={dia.fecha}
                      className={`px-3 py-2 text-center text-xs ${
                        excede ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {totales?.bebidas ?? 0}
                      {excede && (
                        <span className="block text-[9px] text-red-500">
                          excede comida
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GrillaSemanal;
